import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * PagineDiColonne — la griglia dell'Agenda (stile «Console») non scorre mai
 * di lato: mostra tante colonne quante ne entrano, e le altre stanno nelle
 * pagine dietro le frecce ‹ ›.
 *
 * Decisione di Alex, 22/10/2026 («Pagine di colonne»): a 104 px per colonna,
 * a 1440 px circa dieci agende, a 1024 circa sette, a 390 una o due; le
 * frecce e «agende 1-10 di 11» per le altre; mai scorrimento di lato, a
 * nessuna larghezza; le agende scelte con «Agende» restano quelle. Prima la
 * griglia teneva ogni colonna a 104 px e scorreva di lato: con undici agende
 * in sede a 1440 px il rilascio del 19/10 si e' fermato su <div.ag-corpo>
 * «scorre in orizzontale (1200px dentro 1146px)», e dichiararlo scorrevole
 * avrebbe reso la prova piu' permissiva.
 *
 * QUANTE ENTRANO si misura sulla pagina, non si calcola dalla larghezza
 * della finestra: il pannello a destra, il menu e la barra di scorrimento
 * verticale tolgono spazio in modi che cambiano da una schermata all'altra.
 * La misura e' quella del contenitore che scorre in verticale (clientWidth,
 * senza la barra), meno la colonna delle ore, diviso la larghezza minima di
 * una colonna: tutte e due le prende dalle variabili di .ag-wrap
 * (--ag-ore-larghezza, --ag-colonna-minima), cosi' il numero di colonne e la
 * larghezza minima del CSS non possono dire due cose diverse.
 *
 * Non c'e' nessuna regola dell'agenda qui: quali agende lavorano quel giorno,
 * quali sono state scelte, come si chiamano le decidono le rotte e la pagina.
 * Qui si decide solo quante se ne vedono insieme e da quale.
 */

/* I ripieghi: gli stessi valori di .ag-wrap, se il foglio non si legge. */
const ORE_PREDEFINITA = 56;
const COLONNA_PREDEFINITA = 104;

const numeroDa = (testo, ripiego) => {
    const n = parseFloat(testo);
    return Number.isFinite(n) && n > 0 ? n : ripiego;
};

/**
 * @param {number} totale quante colonne ci sarebbero in tutto
 * @returns {{
 *   osserva: (el: HTMLElement|null) => void,   ref del contenitore da misurare
 *   perPagina: number,       quante colonne entrano (almeno una)
 *   inizio: number,          indice della prima colonna a video
 *   fine: number,            indice dopo l'ultima colonna a video
 *   conPagine: boolean,      vero se non entrano tutte
 *   indietro: () => void, avanti: () => void,
 *   porta: (indice: number) => void,   porta in vista la colonna d'indice dato
 * }}
 */
export function usePagineDiColonne(totale) {
    /* Null finche' il contenitore non e' stato misurato: fino ad allora le
       colonne si mostrano tutte, e la misura arriva prima del disegno (il
       callback del ref gira nella stessa fase di commit). */
    const [misurata, setMisurata] = useState(null);
    const [primaScelta, setPrimaScelta] = useState(0);
    const osservatore = useRef(null);

    const osserva = useCallback((el) => {
        if (osservatore.current) { osservatore.current.disconnect(); osservatore.current = null; }
        if (!el) return;
        const misura = () => {
            const stile = window.getComputedStyle(el);
            const ore = numeroDa(stile.getPropertyValue('--ag-ore-larghezza'), ORE_PREDEFINITA);
            const minima = numeroDa(stile.getPropertyValue('--ag-colonna-minima'), COLONNA_PREDEFINITA);
            const quante = Math.max(1, Math.floor((el.clientWidth - ore) / minima));
            setMisurata((prima) => (prima === quante ? prima : quante));
        };
        misura();
        if (typeof ResizeObserver === 'function') {
            osservatore.current = new ResizeObserver(misura);
            osservatore.current.observe(el);
        }
    }, []);
    useEffect(() => () => { if (osservatore.current) osservatore.current.disconnect(); }, []);

    const perPagina = misurata == null ? Math.max(1, totale) : misurata;
    /* L'inizio scelto non sta mai oltre l'ultima pagina piena: se le colonne
       calano (un'agenda tolta da «Agende») o la finestra si allarga, si
       torna indietro da soli invece di lasciare una pagina mezza vuota. */
    const ultimo = Math.max(0, totale - perPagina);
    const inizio = Math.min(Math.max(0, primaScelta), ultimo);
    const fine = Math.min(totale, inizio + perPagina);

    const avanti = useCallback(() => setPrimaScelta(Math.min(inizio + perPagina, ultimo)), [inizio, perPagina, ultimo]);
    const indietro = useCallback(() => setPrimaScelta(Math.max(0, inizio - perPagina)), [inizio, perPagina]);
    /* Porta in vista una colonna col MINIMO spostamento: se sta prima della
       pagina diventa la prima, se sta dopo diventa l'ultima, se c'e' gia' non
       si muove niente. Serve quando una colonna si aggiunge da «Agende», dopo
       «Prenota», e quando il pannello a destra stringe la griglia e spinge
       fuori la colonna dell'appuntamento aperto. */
    const porta = useCallback((indice) => {
        if (indice < 0 || indice >= totale) return;
        if (indice >= inizio && indice < inizio + perPagina) return;
        setPrimaScelta(indice < inizio ? indice : Math.max(0, indice - perPagina + 1));
    }, [totale, inizio, perPagina]);

    return { osserva, perPagina, inizio, fine, conPagine: totale > perPagina, indietro, avanti, porta };
}

/**
 * Le frecce e il conto, «agende 1-10 di 11». Non disegna niente se le
 * colonne entrano tutte. `cosa` e' il plurale di quello che si pagina
 * («agende», «giorni»): nel conto e nei nomi accessibili dei pulsanti.
 */
export function NavigazionePagine({ cosa, pagine, totale }) {
    if (!pagine.conPagine) return null;
    const prima = pagine.inizio + 1;
    const ultima = pagine.fine;
    const Cosa = cosa.charAt(0).toUpperCase() + cosa.slice(1);
    return (
        <div className="ag-pagine" role="group" aria-label={`Pagine di ${cosa}`} data-pagine={cosa}>
            <button type="button" className="ag-freccia" data-pagina="indietro"
                    disabled={pagine.inizio <= 0} onClick={pagine.indietro}
                    aria-label={`${Cosa} precedenti`}>
                <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className="ag-pagine-conto rm-numeri" aria-live="polite">
                {cosa} {prima}-{ultima} di {totale}
            </span>
            <button type="button" className="ag-freccia" data-pagina="avanti"
                    disabled={pagine.fine >= totale} onClick={pagine.avanti}
                    aria-label={`${Cosa} ${cosa === 'giorni' ? 'successivi' : 'successive'}`}>
                <ChevronRight size={16} aria-hidden="true" />
            </button>
        </div>
    );
}
